import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

// -----------------------------------------------------------------------
// LegendFaqPanel — modal (desktop) / bottom-sheet (mobile) explaining the
// battlefield to first-time viewers. Updated for Phase 7 modern-military
// rework (soldiers, rifles, trenches, barbed wire, nuclear missiles).
// -----------------------------------------------------------------------

const LEGEND = [
  { id: "green-soldier", label: "Green soldier (buy order)", text: "A resting BUY order. Every green humanoid soldier standing in the trenches = open bid volume at a specific price. More soldiers = more buy pressure. They wear green fatigues and carry rifles.", svg: <BullSvg /> },
  { id: "red-soldier", label: "Red soldier (sell order)", text: "A resting SELL order. Every red humanoid soldier on the enemy side = open ask volume. More soldiers = more sell pressure. Red fatigues, same rifle.", svg: <BearSvg /> },
  { id: "bull-territory", label: "Bull front (left half)", text: "Bull territory. Trenches, sandbags, and green flags. Everything on this side of the front line is bid space — buy orders.", svg: <GrassSvg /> },
  { id: "bear-territory", label: "Bear front (right half)", text: "Bear territory. Scorched dirt, red banners, and blast craters. Everything here is ask space — sell orders.", svg: <DirtSvg /> },
  { id: "trench", label: "Trench line", text: "Zig-zag dug-in defensive positions along each front. Soldiers spawn and take cover here. The rows closest to no-man's-land carry the best bid / best ask; rows further back are deeper (worse) prices.", svg: <TrenchSvg /> },
  { id: "barbed-wire", label: "Barbed wire", text: "Strung along the outer edges of each trench line. Marks the killzone between the two armies. Pure atmosphere — no data attached.", svg: <WireSvg /> },
  { id: "front-line", label: "Glowing front line", text: "The mid-price boundary between the two armies. It shifts LEFT or RIGHT depending on which side has more standing volume. Bulls gaining ground = line drifts right.", svg: <FrontLineSvg /> },
  { id: "crater", label: "Blast crater", text: "Leftover from previous nuke strikes and heavy fire. Scars the terrain on both sides. Decorative — a visual reminder of past engagements.", svg: <CraterSvg /> },
  { id: "missile", label: "Nuclear missile (at HQ)", text: "A tall missile on a launch pad at the deep-rear of each base. Stays ARMED and READY until an extreme trade (top 0.1%) triggers a launch. See the Nuke Status HUD for cooldown state.", svg: <MissileSvg /> },
  { id: "mushroom", label: "Mushroom cloud", text: "What you see when a nuke fires. Massive blast wave damages the front-line soldiers on the LOSING side (opposite the launcher). Cools down for ~30s before the same silo can fire again.", svg: <MushroomSvg /> },
  { id: "tracer", label: "Rifle tracer / projectile", text: "On every trade, a soldier fires. A quick green or red tracer streaks across no-man's-land toward the losing side. Small trades = single shot. Aggressive trades = volley of shots.", svg: <ClashSvg /> },
  { id: "ragdoll", label: "Fallen soldier (KIA)", text: "When a soldier is hit by an aggressive trade, they crumple and fall. That specific chunk of liquidity just got filled. The KIA counter in the bottom-left tracks kills over the last 60 seconds.", svg: <VanishSvg /> },
  { id: "flag", label: "Base flag & plaque", text: "Planted at each HQ. Green 'BULLS' flag on the left rear, red 'BEARS' flag on the right rear. Marks where the nuclear silos live.", svg: <FlagSvg /> },
  { id: "buy-wall", label: "BUY WALL / SELL WALL panels", text: "Total notional USD sitting in the top-20 bid (buy) and ask (sell) levels. Updates live. Tells you the raw depth on each side of the book.", svg: <WallsSvg /> },
  { id: "market-feed", label: "Market Feed (bottom-right)", text: "The last 5 aggressive (top-10%) trades. Each row shows direction, notional size, and price — the highlight reel of significant orders hitting the book.", svg: <FeedSvg /> },
  { id: "tape", label: "Right-side scrolling tape", text: "The raw list of EVERY trade as it happens (not just aggressive ones). Complements the battlefield with hard numbers.", svg: <TapeSvg /> },
];

const FAQ = [
  { q: "Is this real data?", a: "Yes. All data is live from Binance's public market feed (WebSocket). No mocks, no simulation. If Binance goes down, the scene freezes and you'll see a 'reconnecting' banner." },
  { q: 'What is an "order book"?', a: "Every exchange has a list of standing buy orders (bids) and sell orders (asks) at various prices. The order book is a snapshot of that queue. This site turns that queue into two armies of soldiers holding trench lines." },
  { q: "Why are the soldiers shooting each other?", a: "Every trade on Binance fires a shot. When a BUY order fills, a green soldier fires a tracer at the red side — a red soldier drops. When a SELL order fills, red fires at green. Small trades = 1 shot. Aggressive (top 10%) trades = a volley. The KIA counter in the bottom-left tallies losses over the last 60s." },
  { q: "What are those big missiles at the back?", a: "Nuclear silos. One per base. They sit ARMED and READY until an EXTREME trade (top 0.1% of the last 100 trades) triggers a launch. The missile arcs across no-man's-land, detonates over the losing side, and a mushroom cloud + blast wave wipes out several front-line soldiers. Each silo has its own ~30s cooldown — check the Nuke Status HUD." },
  { q: "How do I trigger a nuke myself?", a: "You don't — real Binance traders do. The threshold is dynamic: quantities above the 99th percentile of recent trades count as extreme. Big whales moving big volume = big booms." },
  { q: "What makes the front line move left or right?", a: "Sum of standing bid notional vs standing ask notional. When bids > asks, the front line drifts RIGHT (bulls take territory). When asks > bids, LEFT. It smooths over ~2 seconds so the shift feels natural." },
  { q: "Why do fallen soldiers keep respawning?", a: "Kills reflect trades that just executed. But the army itself always mirrors the CURRENT order book, so when new orders arrive at a price level, fresh soldiers march up from the rear trenches to fill in. Deaths are dramatic; army composition is the truth." },
  { q: 'What determines who "wins"?', a: "Nothing, literally. This is a visualization, not a game. If more buys come in than sells, the price moves up and the front line drifts right — that's the only 'winning'." },
  { q: "Where does the mid price come from?", a: "It's the midpoint between the best bid (highest buy) and best ask (lowest sell) on Binance's spot book, updated every 100ms." },
  { q: "What's the difference between the KIA counter and the Nuke HUD?", a: "KIA counter = rolling 60s tally of ALL kills, one per aggressive trade. Nuke HUD = status of the two nuclear silos (READY / ARMING / COOLDOWN). Nukes fire only on top-0.1% extreme trades, so most sessions the silos stay READY." },
  { q: "Can I pick pairs that aren't on Binance?", a: "No. The picker only shows pairs Binance supports (~1,367 spot pairs). If you pick two coins that don't have a direct market, we'll offer to show one leg via USDT." },
  { q: "Why 20 price levels per side?", a: "That's the depth Binance streams in real time. Beyond that isn't as market-relevant." },
  { q: "Is my trading data being tracked?", a: "No trading data exists here — the site never asks for your accounts. No auth, no wallet connect. Only public order book data is displayed." },
  { q: "What are the HQ and Orbit toggles?", a: "HQ turns high-quality post-processing (bloom + SSAO) on/off. It's ON by default on desktop for cinematic vibes; turn it off if your GPU is chugging. Orbit slowly rotates the camera around the battlefield for a hands-off cinematic view. Both persist across page reloads." },
  { q: "Can I share what I'm watching?", a: "Yes — hit the Share button. It copies a link with the current pair. Anyone opening it lands on the exact same pair." },
  { q: 'What\'s the "War Room"?', a: "A grid view where you watch multiple pairs fight in parallel. Good for scanning several markets at once." },
];

export default function LegendFaqPanel({ open, onOpenChange }) {
  const isMobile = useIsMobile();
  const [openFaqIdx, setOpenFaqIdx] = useState(null);

  useEffect(() => {
    if (open) {
      try { localStorage.setItem("cb_hasSeenFAQ", "1"); } catch { /* ignore */ }
    }
  }, [open]);

  const body = (
    <div className="flex flex-col gap-6 overflow-y-auto max-h-full pb-8" data-testid="legend-faq-content">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle className="w-4 h-4 text-[#FFE480]" />
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tighter text-white" data-testid="legend-faq-title">
            What am I looking at?
          </h2>
        </div>
        <p className="font-mono-tac text-[11px] uppercase tracking-[0.24em] text-slate-400" data-testid="legend-faq-subtitle">
          A quick guide to the battlefield
        </p>
      </div>

      <section data-testid="legend-section">
        <h3 className="font-mono-tac text-[10px] uppercase tracking-[0.32em] text-[#FFE480] mb-3 border-b border-white/10 pb-2">
          Legend
        </h3>
        <ul className="flex flex-col gap-3">
          {LEGEND.map((item) => (
            <li key={item.id} className="flex items-start gap-3" data-testid={`legend-row-${item.id}`}>
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-white/[0.03] border border-white/10 rounded-sm">
                {item.svg}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm font-semibold text-white leading-tight">{item.label}</div>
                <p className="font-mono-tac text-[11px] text-slate-400 leading-relaxed mt-0.5">{item.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section data-testid="faq-section">
        <h3 className="font-mono-tac text-[10px] uppercase tracking-[0.32em] text-[#FFE480] mb-3 border-b border-white/10 pb-2">
          FAQ
        </h3>
        <div className="flex flex-col gap-1">
          {FAQ.map((entry, i) => {
            const isOpen = openFaqIdx === i;
            return (
              <div key={i} className="border border-white/5 rounded-sm bg-white/[0.02]" data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-white/5 transition-colors"
                  onClick={() => setOpenFaqIdx(isOpen ? null : i)}
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                >
                  <span className="font-display text-sm text-white leading-tight">{entry.q}</span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1">
                    <p className="font-mono-tac text-[11px] text-slate-300 leading-relaxed" data-testid={`faq-answer-${i}`}>
                      {entry.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <p className="font-mono-tac text-[10px] text-slate-500 uppercase tracking-[0.24em] text-center pt-2">
        Powered by Binance public data · No accounts, no tracking
      </p>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="!bg-[#010308]/95 !border-t !border-white/10 backdrop-blur-xl max-h-[85vh] p-5 overflow-hidden flex flex-col"
          data-testid="legend-faq-sheet"
          style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
        >
          <SheetTitle className="sr-only">What am I looking at? Legend and FAQ</SheetTitle>
          <SheetDescription className="sr-only">
            A quick guide explaining every element on the battlefield and answers to common questions.
          </SheetDescription>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-[#010308]/95 !border !border-white/10 backdrop-blur-xl !rounded-sm max-w-2xl w-full max-h-[85vh] p-6 overflow-hidden flex flex-col"
        data-testid="legend-faq-dialog"
      >
        <DialogTitle className="sr-only">What am I looking at? Legend and FAQ</DialogTitle>
        <DialogDescription className="sr-only">
          A quick guide explaining every element on the battlefield and answers to common questions.
        </DialogDescription>
        {body}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Inline SVG glyphs — tiny 32×32-friendly renderings.
// -----------------------------------------------------------------------

function BullSvg() {
  // Green humanoid soldier holding a rifle, facing right toward the front line.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* helmet */}
      <rect x="12" y="4" width="8" height="4" fill="#3a5a2a" />
      <rect x="11" y="7" width="10" height="2" fill="#3a5a2a" />
      {/* head */}
      <rect x="13" y="9" width="6" height="4" fill="#e2c9a3" />
      {/* torso (green fatigues) */}
      <rect x="11" y="13" width="10" height="9" fill="#4a8c39" />
      {/* rifle — barrel points right */}
      <rect x="20" y="15" width="9" height="1.5" fill="#2a1a0a" />
      <rect x="18" y="14" width="3" height="4" fill="#5a3a20" />
      {/* legs */}
      <rect x="12" y="22" width="3" height="7" fill="#3a5a2a" />
      <rect x="17" y="22" width="3" height="7" fill="#3a5a2a" />
      {/* boots */}
      <rect x="12" y="28" width="3.5" height="2" fill="#1a1a1a" />
      <rect x="17" y="28" width="3.5" height="2" fill="#1a1a1a" />
    </svg>
  );
}
function BearSvg() {
  // Red humanoid soldier holding a rifle, facing left toward the front line.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* helmet */}
      <rect x="12" y="4" width="8" height="4" fill="#7a1a10" />
      <rect x="11" y="7" width="10" height="2" fill="#7a1a10" />
      {/* head */}
      <rect x="13" y="9" width="6" height="4" fill="#e2c9a3" />
      {/* torso (red fatigues) */}
      <rect x="11" y="13" width="10" height="9" fill="#c73a2a" />
      {/* rifle — barrel points left */}
      <rect x="3" y="15" width="9" height="1.5" fill="#2a1a0a" />
      <rect x="11" y="14" width="3" height="4" fill="#5a3a20" />
      {/* legs */}
      <rect x="12" y="22" width="3" height="7" fill="#7a1a10" />
      <rect x="17" y="22" width="3" height="7" fill="#7a1a10" />
      {/* boots */}
      <rect x="12" y="28" width="3.5" height="2" fill="#1a1a1a" />
      <rect x="17" y="28" width="3.5" height="2" fill="#1a1a1a" />
    </svg>
  );
}
function GrassSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="0" y="0" width="32" height="32" fill="#4a8c39" />
      <rect x="3" y="7" width="3" height="3" fill="#3d7530" />
      <rect x="18" y="14" width="3" height="3" fill="#3d7530" />
      <rect x="9" y="22" width="3" height="3" fill="#3d7530" />
      <rect x="24" y="6" width="3" height="3" fill="#3d7530" />
    </svg>
  );
}
function DirtSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="0" y="0" width="32" height="32" fill="#8a6b3f" />
      <rect x="4" y="5" width="3" height="3" fill="#6f5530" />
      <rect x="20" y="12" width="3" height="3" fill="#6f5530" />
      <rect x="11" y="24" width="3" height="3" fill="#6f5530" />
      <rect x="26" y="21" width="3" height="3" fill="#6f5530" />
    </svg>
  );
}
function FrontLineSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="0" y="0" width="16" height="32" fill="#4a8c39" />
      <rect x="16" y="0" width="16" height="32" fill="#8a6b3f" />
      <path d="M 16 0 Q 12 8, 16 16 T 16 32" stroke="#fff8d0" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}
function OutpostSvg() {
  return null;
}

function FlagSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="14" y="4" width="2" height="26" fill="#6a6a6a" />
      <rect x="16" y="4" width="12" height="7" fill="#ff3020" />
      <rect x="6" y="16" width="20" height="4" fill="#ffffff" stroke="#c9291b" strokeWidth="1" />
      <text x="16" y="19.5" fontSize="4.5" fontWeight="bold" fill="#c9291b" textAnchor="middle" fontFamily="sans-serif">BEARS</text>
    </svg>
  );
}
function TreeSvg() {
  return null;
}
function WaterSvg() {
  return null;
}
function ClashSvg() {
  // Rifle tracer streaking across no-man's-land.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="2" y="14" width="6" height="4" fill="#4a8c39" />
      <rect x="24" y="14" width="6" height="4" fill="#c73a2a" />
      {/* tracer streak */}
      <line x1="8" y1="16" x2="24" y2="16" stroke="#ffe860" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="16" x2="24" y2="16" stroke="#ffffff" strokeWidth="0.75" strokeLinecap="round" opacity="0.85" />
      {/* muzzle flash */}
      <circle cx="8" cy="16" r="2.2" fill="#ffe860" opacity="0.85" />
    </svg>
  );
}
function VanishSvg() {
  // Fallen (ragdoll) soldier with a small dust puff.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* body prone */}
      <rect x="6" y="18" width="14" height="4" fill="#c73a2a" opacity="0.55" />
      {/* head */}
      <circle cx="6" cy="20" r="2.2" fill="#e2c9a3" opacity="0.55" />
      {/* helmet next to head, knocked off */}
      <rect x="10" y="16" width="4" height="2" fill="#7a1a10" opacity="0.55" />
      {/* dust puff */}
      <circle cx="22" cy="24" r="2.4" fill="#d6bb8c" opacity="0.7" />
      <circle cx="26" cy="26" r="1.6" fill="#d6bb8c" opacity="0.5" />
      <circle cx="19" cy="27" r="1.2" fill="#d6bb8c" opacity="0.4" />
    </svg>
  );
}
function TrenchSvg() {
  // Zig-zag trench cross-section.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="0" y="18" width="32" height="10" fill="#5a4a2a" />
      <path d="M 0 18 L 6 18 L 8 22 L 14 22 L 16 18 L 22 18 L 24 22 L 30 22 L 32 18" fill="#1a1408" />
      {/* sandbags */}
      <rect x="1" y="14" width="4" height="3" rx="1" fill="#a08a5a" />
      <rect x="6" y="14" width="4" height="3" rx="1" fill="#a08a5a" />
      <rect x="22" y="14" width="4" height="3" rx="1" fill="#a08a5a" />
      <rect x="27" y="14" width="4" height="3" rx="1" fill="#a08a5a" />
    </svg>
  );
}
function WireSvg() {
  // Barbed wire strand.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* posts */}
      <rect x="4" y="8" width="1.5" height="20" fill="#3a2a1a" />
      <rect x="26" y="8" width="1.5" height="20" fill="#3a2a1a" />
      {/* main wire */}
      <line x1="4" y1="14" x2="28" y2="14" stroke="#c9c9c9" strokeWidth="1" />
      <line x1="4" y1="20" x2="28" y2="20" stroke="#c9c9c9" strokeWidth="1" />
      {/* barbs */}
      {[8, 14, 20, 26].map((x) => (
        <g key={x}>
          <line x1={x} y1="12" x2={x} y2="16" stroke="#c9c9c9" strokeWidth="1" />
          <line x1={x - 2} y1="14" x2={x + 2} y2="14" stroke="#c9c9c9" strokeWidth="1" />
          <line x1={x} y1="18" x2={x} y2="22" stroke="#c9c9c9" strokeWidth="1" />
          <line x1={x - 2} y1="20" x2={x + 2} y2="20" stroke="#c9c9c9" strokeWidth="1" />
        </g>
      ))}
    </svg>
  );
}
function MissileSvg() {
  // Nuclear missile on a launch pad.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* pad */}
      <rect x="6" y="26" width="20" height="3" fill="#4a4a4a" />
      {/* body */}
      <rect x="13" y="8" width="6" height="18" fill="#e6e6e6" stroke="#7a7a7a" strokeWidth="0.75" />
      {/* nosecone */}
      <polygon points="13,8 16,2 19,8" fill="#c73a2a" />
      {/* fins */}
      <polygon points="13,22 10,26 13,26" fill="#7a1a10" />
      <polygon points="19,22 22,26 19,26" fill="#7a1a10" />
      {/* radiation warning stripes */}
      <rect x="13" y="14" width="6" height="1" fill="#ffe860" />
      <rect x="13" y="18" width="6" height="1" fill="#ffe860" />
    </svg>
  );
}
function MushroomSvg() {
  // Mushroom cloud silhouette.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      {/* ground blast */}
      <ellipse cx="16" cy="27" rx="12" ry="2" fill="#8a3a1a" opacity="0.6" />
      {/* stem */}
      <rect x="13" y="14" width="6" height="12" fill="#6a5040" opacity="0.85" />
      {/* cap */}
      <ellipse cx="16" cy="12" rx="12" ry="7" fill="#a06a3a" opacity="0.85" />
      <ellipse cx="16" cy="10" rx="8" ry="4" fill="#c98a5a" opacity="0.85" />
      <ellipse cx="16" cy="8" rx="4" ry="2" fill="#e6b078" opacity="0.85" />
    </svg>
  );
}
function CraterSvg() {
  // Blast crater on the ground.
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="0" y="0" width="32" height="32" fill="#8a6b3f" />
      <ellipse cx="16" cy="17" rx="12" ry="7" fill="#3a2a15" />
      <ellipse cx="16" cy="16" rx="9" ry="4.5" fill="#1a0e05" />
      {/* debris chunks */}
      <rect x="4" y="6" width="2" height="2" fill="#5a4a2a" />
      <rect x="26" y="8" width="2" height="2" fill="#5a4a2a" />
      <rect x="7" y="26" width="2" height="2" fill="#5a4a2a" />
      <rect x="24" y="25" width="2" height="2" fill="#5a4a2a" />
    </svg>
  );
}
function WallsSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="4" y="8" width="8" height="20" fill="#00FFA3" opacity="0.7" />
      <rect x="20" y="4" width="8" height="24" fill="#FF2A2A" opacity="0.7" />
      <rect x="14" y="4" width="4" height="24" fill="none" stroke="#ffe480" strokeWidth="1" />
    </svg>
  );
}
function FeedSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="2" y="4" width="28" height="6" fill="#00FFA3" opacity="0.7" />
      <rect x="2" y="13" width="28" height="6" fill="#FF2A2A" opacity="0.7" />
      <rect x="2" y="22" width="28" height="6" fill="#00FFA3" opacity="0.7" />
    </svg>
  );
}
function TapeSvg() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" aria-hidden="true">
      <rect x="4" y="4" width="24" height="24" fill="#010308" stroke="#334155" strokeWidth="1" />
      <line x1="6" y1="10" x2="26" y2="10" stroke="#00FFA3" strokeWidth="1.5" />
      <line x1="6" y1="16" x2="24" y2="16" stroke="#00FFA3" strokeWidth="1.5" />
      <line x1="6" y1="22" x2="22" y2="22" stroke="#FF2A2A" strokeWidth="1.5" />
    </svg>
  );
}

// -----------------------------------------------------------------------
// Trigger — small "?" button used elsewhere in the HUD.
// -----------------------------------------------------------------------

export function LegendFaqTrigger({ onClick, iconOnly = false, pulse = false }) {
  const pulseClass = pulse
    ? "before:absolute before:inset-0 before:rounded-sm before:animate-ping before:bg-[#FFE480]/30 relative overflow-visible"
    : "";
  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={`cb-hud-chip flex items-center justify-center w-11 h-11 hover:bg-white/5 transition-colors shrink-0 ${pulseClass}`}
        aria-label="What am I looking at? Open legend and FAQ"
        data-testid="legend-faq-trigger"
        data-pulse={pulse ? "1" : "0"}
      >
        <HelpCircle className="w-4 h-4 text-white" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cb-hud-chip flex items-center gap-2 px-4 py-2 hover:bg-white/5 transition-colors group ${pulseClass}`}
      aria-label="What am I looking at? Open legend and FAQ"
      data-testid="legend-faq-trigger"
      data-pulse={pulse ? "1" : "0"}
    >
      <HelpCircle className="w-4 h-4 text-white/80 group-hover:text-white" />
      <span className="font-mono-tac text-[10px] uppercase tracking-[0.28em] text-white">Legend</span>
    </button>
  );
}
